import React, { memo } from 'react';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import Icon from '@mdi/react';
import { mdiAlertCircleOutline } from '@mdi/js';
import { Card, Statistic, Col, Typography, Skeleton } from 'antd';
import { NameDefinition } from 'components/NameDefinition';
import Ninja from '@ninja';

export const trafficRepotByPeriodMetrics = {
  sessions: {
    title: 'Sessions',
    key: 'sessions',
    color: '#2c7df0',
    bg: '#eff7ffff',
    format: (val) => getFormattedNumber(val),
  },
  unique_sessions: {
    title: (
      <NameDefinition
        textSize='!font-normal'
        name='Unique Sessions'
        definition='Number of distinct sessions started by users.'
      />
    ),
    key: 'unique_sessions',
    color: Ninja.colors.success,
    bg: Ninja.colors.bgColorSuccess,
    format: (val) => getFormattedNumber(val),
  },
  avg_session_duration: {
    title: (
      <NameDefinition
        textSize='!font-normal'
        name='Avg. Session Duration'
        definition='Average length of a user session, showing how long users stay on site. total session duration (in seconds) / total sessions.'
      />
    ),
    key: 'avg_session_duration',
    color: Ninja.colors.warning,
    bg: Ninja.colors.bgColorWarning,
    format: (val) => val || '-',
  },
  sessions_per_user: {
    title: (
      <NameDefinition
        textSize='!font-normal'
        name='Sessions per User'
        definition='Average number of sessions started by each unique user within the selected period. total sessions / unique users.'
      />
    ),
    key: 'sessions_per_user',
    color: Ninja.colors.info,
    bg: Ninja.colors.bgColorInfo,
    format: (val) => getFormattedNumber(val),
  },
  bounce_rate: {
    title: (
      <NameDefinition
        textSize='!font-normal'
        name='Bounce Rate'
        definition='Percentage of sessions with only one page view, calculated as (single-page sessions ÷ total sessions) × 100.'
      />
    ),
    key: 'bounce_rate',
    color: Ninja.colors.error,
    bg: Ninja.colors.bgColorError,
    format: (val) => `${getFormattedNumber(val)}%`,
  },
};

export const EmptyState = ({ message }) => (
  <div
    className='w-full flex flex-col h-full justify-center items-center'
    style={{ height: '300px' }}
  >
    <Icon path={mdiAlertCircleOutline} size={1.4} className='p-1 rounded-md text-color-light' />
    <Typography className='text-sm font-normal text-color-light'>{message}</Typography>
  </div>
);

export const TrafficRepotByPeriodCard = memo(
  ({ metricKey, metric, total, isActive, onClick, loading }) => {
    const { title, change, color, bg, format } = metric;
    const isPositive = change >= 0;

    return (
      <Col key={metricKey}>
        <Card
          hoverable
          className='w-full min-w-48 transition-all duration-200 cursor-pointer'
          onClick={() => onClick(metricKey)}
          style={{
            border: isActive ? `1px solid ${color}` : '1px solid #e5e5e5de',
            background: isActive ? bg : '#fff',
          }}
        >
          <Skeleton paragraph={{ rows: 2 }} title={false} active loading={loading}>
            <Statistic
              title={
                <Typography className='text-sm font-firaGoMedium text-info'>{title}</Typography>
              }
              value={<span>{format ? format(total) : total}</span>}
              formatter={(val) => (
                <span className='sm:text-3xl text-lg font-medium text-color-primary-text'>
                  {val}
                </span>
              )}
              suffix={
                change && (
                  <span
                    className={`${
                      isPositive ? 'text-green' : 'text-red'
                    } font-medium text-base ml-2`}
                  >
                    {isPositive ? `▲ ${change}%` : `▼ ${Math.abs(change)}%`}
                  </span>
                )
              }
            />
          </Skeleton>
        </Card>
      </Col>
    );
  }
);
